import type { ComponentProps } from "react";

import {
  faDownload,
  faExclamationTriangle,
  faEye,
  faFile,
  faSpinner,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";
import { twJoin } from "tailwind-merge";

import { useFile } from "src/api/filesApi/queries";
import { DisplayDate } from "src/base-components/Date";
import {
  FileAttributes,
  GetFileEnvFilesFileIdGetEnvEnum,
} from "src/clients/files";
import { Icon, IconButton } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { Tooltip } from "src/design-system/Tooltip";
import { FileViewer } from "src/files/FileViewer";
import { Environment } from "src/hooks/useEnvironment";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { FILE_TYPES, humanReadableSize } from "src/utils/files";

/** Figma: https://www.figma.com/design/BxvmQsi7FfcW3KDfY7qXab/Agents?node-id=2405-43007&m=dev */

type FileCardBaseProps = {
  /** Shows a spinner and "Uploading..." instead of file metadata. */
  isUploading?: boolean;
  /** Callback when the delete button is clicked. Omit to hide the delete action. */
  onDelete?: () => void;
  /** Dims the card and disables action buttons (except view). */
  disabled?: boolean;
  /** Content rendered next to the file name (e.g. a "New" pill). */
  badge?: React.ReactNode;
  /** When true, appends the upload date to the metadata line (remote files only). */
  showCreatedAt?: boolean;
  /** Additional action elements rendered after the built-in actions.
   *  Pass a function to receive the resolved file name. */
  rightSlot?:
    | React.ReactNode
    | ((fileInfo: { name: string }) => React.ReactNode);
  dataLoc?: string;
  /** Called with the resolved file when the view button is clicked.
   *  When provided, replaces the built-in FileViewer floating window. */
  onView?: (file: FileAttributes | File) => void;
  floatingWindowPlacement?: ComponentProps<typeof FileViewer>["placement"];
  floatingWindowOffset?: ComponentProps<typeof FileViewer>["offset"];
};

type LocalFileCardProps = FileCardBaseProps & {
  /** Local File object for immediate display (no API fetch). */
  file: File;
  fileId?: never;
  fileEnv?: never;
};

type RemoteFileCardProps = FileCardBaseProps & {
  /** API file ID — file metadata is fetched from the server. */
  fileId: string;
  /** Environment for API fetch. */
  fileEnv: Environment;
  /** Optional local File object as fallback when API data is unavailable. */
  file?: File;
};

type FileCardProps = LocalFileCardProps | RemoteFileCardProps;

type BaseCardProps = {
  icon: React.ReactNode;
  title: React.ReactNode;
  badge?: React.ReactNode;
  subtitle?: React.ReactNode;
  rightSlot?: React.ReactNode;
  dataLoc?: string;
};

const BaseCard: React.FC<BaseCardProps> = ({
  icon,
  title,
  badge,
  subtitle,
  rightSlot,
  dataLoc,
}) => (
  <div
    className="flex w-full items-center rounded-lg border border-gray-200 bg-white px-2.5 py-2"
    data-loc={dataLoc}
  >
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="flex items-center gap-1">
        {icon}
        <div className="min-w-0 flex-1">{title}</div>
        {badge}
        {rightSlot}
      </div>
      {subtitle && (
        <div className="flex items-center gap-1">
          <div className="w-5 shrink-0" />
          {subtitle}
        </div>
      )}
    </div>
  </div>
);

type FileCardContentProps = {
  name: string;
  metadata: React.ReactNode;
  download?: { href: string; fileName: string } | { file: File };
  viewerFile: Parameters<typeof FileViewer>[0]["file"] | File;
  onDelete?: () => void;
  onView?: (file: FileAttributes | File) => void;
  disabled?: boolean;
  badge?: React.ReactNode;
  rightSlot?: React.ReactNode;
  dataLoc?: string;
  floatingWindowPlacement?: ComponentProps<typeof FileViewer>["placement"];
  floatingWindowOffset?: ComponentProps<typeof FileViewer>["offset"];
};

const FileCardContent: React.FC<FileCardContentProps> = ({
  name,
  metadata,
  download,
  viewerFile,
  onDelete,
  onView,
  disabled,
  rightSlot,
  badge,
  dataLoc,
  floatingWindowPlacement,
  floatingWindowOffset,
}) => {
  const viewIconButton = (
    <IconButton
      aria-label="View file"
      dataLoc={dataLoc ? `${dataLoc}-view` : undefined}
      icon={faEye}
      size="xs"
      onClick={() => viewerFile && onView?.(viewerFile)}
    />
  );

  const rightSlotContent = (
    <>
      {download && (
        <DownloadAction
          dataLoc={dataLoc}
          disabled={disabled}
          download={download}
        />
      )}
      {onView ? (
        <Tooltip title="View" asChild>
          {viewIconButton}
        </Tooltip>
      ) : (
        <FileViewer
          file={viewerFile}
          offset={floatingWindowOffset}
          placement={floatingWindowPlacement}
        >
          {/* Without the span the onClick handler doesnt get attached properly */}
          <span>
            <Tooltip title="View" asChild>
              {viewIconButton}
            </Tooltip>
          </span>
        </FileViewer>
      )}
      {onDelete && (
        <Tooltip title="Delete" asChild>
          <IconButton
            aria-label="Delete file"
            disabled={disabled}
            icon={faTrashCan}
            size="xs"
            onClick={onDelete}
          />
        </Tooltip>
      )}
      {rightSlot}
    </>
  );

  return (
    <BaseCard
      rightSlot={rightSlotContent}
      badge={badge}
      dataLoc={dataLoc}
      icon={
        <Icon
          icon={faFile}
          size="xs"
          variant={disabled ? "default" : "active"}
        />
      }
      subtitle={
        <span className="font-inter-normal-12px text-gray-500">{metadata}</span>
      }
      title={
        <Tooltip title={name} overflowOnly asChild>
          <span
            className={twJoin(
              "block truncate font-inter-semibold-13px",
              disabled ? "text-gray-500" : "text-gray-800",
            )}
          >
            {name}
          </span>
        </Tooltip>
      }
    />
  );
};

const triggerDownload = (href: string, fileName: string) => {
  const a = document.createElement("a");
  a.href = href;
  a.download = fileName;
  a.click();
};

const downloadLocalFile = (file: File) => {
  const url = URL.createObjectURL(file);
  triggerDownload(url, file.name);
  URL.revokeObjectURL(url);
};

/** Triggers a file download — creates a temporary blob URL for local files, uses the API URL for remote files. */
const DownloadAction: React.FC<{
  download: { href: string; fileName: string } | { file: File };
  disabled?: boolean;
  dataLoc?: string;
}> = ({ download, disabled, dataLoc }) => {
  const isLocalFile = "file" in download;

  const handleClick = () => {
    if (isLocalFile) {
      downloadLocalFile(download.file);
    } else {
      triggerDownload(download.href, download.fileName);
    }
  };

  return (
    <Tooltip title="Download" asChild>
      <IconButton
        aria-label="Download file"
        dataLoc={dataLoc ? `${dataLoc}-download` : undefined}
        disabled={disabled}
        icon={faDownload}
        size="xs"
        onClick={handleClick}
      />
    </Tooltip>
  );
};

export const RemoteFileCardError: React.FC<{
  onDelete?: () => void;
  rightSlot?: React.ReactNode;
  disabled?: boolean;
  dataLoc?: string;
  title: string;
  subtitle: string;
}> = ({ onDelete, rightSlot, disabled, dataLoc, title, subtitle }) => (
  <BaseCard
    rightSlot={
      onDelete || rightSlot ? (
        <>
          {onDelete && (
            <Tooltip title="Delete" asChild>
              <IconButton
                aria-label="Delete file"
                disabled={disabled}
                icon={faTrashCan}
                size="xs"
                onClick={onDelete}
              />
            </Tooltip>
          )}
          {rightSlot}
        </>
      ) : undefined
    }
    dataLoc={dataLoc ? `${dataLoc}-error` : undefined}
    icon={<Icon icon={faExclamationTriangle} size="xs" variant="error" />}
    subtitle={
      <span className="font-inter-normal-12px text-gray-500">{subtitle}</span>
    }
    title={
      <span className="font-inter-semibold-13px text-gray-800">{title}</span>
    }
  />
);

const SkeletonLine: React.FC<{
  fontClass: string;
  width: string;
}> = ({ fontClass, width }) => (
  <span className={twJoin("relative inline-flex items-center", fontClass)}>
    <span className="invisible" aria-hidden>
      &nbsp;
    </span>
    <span
      className={twJoin("absolute inset-y-1 left-0 rounded bg-gray-100", width)}
    />
  </span>
);

export const RemoteFileCardLoading: React.FC<{ dataLoc?: string }> = ({
  dataLoc,
}) => (
  <BaseCard
    dataLoc={dataLoc ? `${dataLoc}-loading` : undefined}
    icon={
      <div className="flex h-5 w-5 shrink-0 items-center justify-center">
        <Skeleton height="h-3" rounded="rounded-full" width="w-3" />
      </div>
    }
    subtitle={<SkeletonLine fontClass="font-inter-normal-12px" width="w-16" />}
    title={<SkeletonLine fontClass="font-inter-semibold-13px" width="w-24" />}
  />
);

const FileCardUploading: React.FC<{ file: File; dataLoc?: string }> = ({
  file,
  dataLoc,
}) => (
  <BaseCard
    dataLoc={dataLoc}
    icon={<Icon icon={faSpinner} size="xs" variant="active" spin />}
    subtitle={
      <span className="font-inter-normal-12px text-gray-500">Uploading...</span>
    }
    title={
      <span className="truncate font-inter-semibold-13px text-gray-800">
        {file.name}
      </span>
    }
  />
);

const FileMetadata: React.FC<{
  contentType: string;
  size: number;
  createdAt?: string;
}> = ({ contentType, size, createdAt }) => {
  const fileType = FILE_TYPES[contentType as keyof typeof FILE_TYPES] || "File";
  const base = [fileType, humanReadableSize(size)].filter(Boolean).join(" • ");
  return (
    <>
      {base}
      {createdAt && (
        <>
          {" "}
          • Added <DisplayDate value={createdAt} />
        </>
      )}
    </>
  );
};

const resolveRightSlot = (
  rightSlot:
    | React.ReactNode
    | ((fileInfo: { name: string }) => React.ReactNode)
    | undefined,
  name: string,
): React.ReactNode => {
  if (typeof rightSlot === "function") {
    return rightSlot({ name });
  }
  return rightSlot;
};

/** Fetches file metadata from the API and renders via FileCardContent. */
const RemoteFileCard: React.FC<{
  fileId: string;
  fileEnv: Environment;
  localFile?: File;
  onDelete?: () => void;
  onView?: (file: FileAttributes | File) => void;
  badge?: React.ReactNode;
  showCreatedAt?: boolean;
  rightSlot?:
    | React.ReactNode
    | ((fileInfo: { name: string }) => React.ReactNode);
  disabled?: boolean;
  dataLoc?: string;
  floatingWindowPlacement?: ComponentProps<typeof FileViewer>["placement"];
  floatingWindowOffset?: ComponentProps<typeof FileViewer>["offset"];
}> = ({
  fileId,
  fileEnv,
  localFile,
  onDelete,
  onView,
  disabled,
  badge,
  showCreatedAt,
  rightSlot,
  dataLoc,
  floatingWindowPlacement,
  floatingWindowOffset,
}) => {
  const { workspace } = useWorkspaceContext();

  const {
    data: fileResponse,
    isLoading,
    error,
  } = useFile(
    fileId,
    fileEnv as GetFileEnvFilesFileIdGetEnvEnum,
    workspace.base_url,
  );

  const fileData = fileResponse?.data?.data;
  const isFileNotFound = isAxiosError(error) && error.response?.status === 404;
  const isForbidden = isAxiosError(error) && error.response?.status === 403;

  if (isLoading) {
    return <RemoteFileCardLoading dataLoc={dataLoc} />;
  }

  if (isForbidden || isFileNotFound || (!fileData && !isLoading)) {
    return (
      <RemoteFileCardError
        dataLoc={dataLoc}
        disabled={disabled}
        rightSlot={resolveRightSlot(rightSlot, "Unknown file")}
        subtitle={
          isForbidden
            ? "You don't have permission to view this file."
            : "File load failed - it might be missing or broken."
        }
        title={isForbidden ? "File access denied" : "File not found"}
        onDelete={onDelete}
      />
    );
  }

  if (fileData?.attributes) {
    const attrs = fileData.attributes;
    return (
      <FileCardContent
        badge={badge}
        dataLoc={dataLoc}
        disabled={disabled}
        download={
          attrs.download_url
            ? { href: attrs.download_url, fileName: attrs.name }
            : undefined
        }
        floatingWindowOffset={floatingWindowOffset}
        floatingWindowPlacement={floatingWindowPlacement}
        rightSlot={resolveRightSlot(rightSlot, attrs.name)}
        metadata={
          <FileMetadata
            contentType={attrs.content_type}
            createdAt={showCreatedAt ? attrs.created_at : undefined}
            size={attrs.size}
          />
        }
        name={attrs.name}
        viewerFile={attrs as Parameters<typeof FileViewer>[0]["file"]}
        onDelete={onDelete}
        onView={onView}
      />
    );
  }

  if (localFile) {
    return (
      <FileCardContent
        badge={badge}
        dataLoc={dataLoc}
        disabled={disabled}
        download={{ file: localFile }}
        floatingWindowOffset={floatingWindowOffset}
        floatingWindowPlacement={floatingWindowPlacement}
        rightSlot={resolveRightSlot(rightSlot, localFile.name)}
        metadata={
          <FileMetadata contentType={localFile.type} size={localFile.size} />
        }
        name={localFile.name}
        viewerFile={localFile}
        onDelete={onDelete}
        onView={onView}
      />
    );
  }

  return null;
};

export const FileCard: React.FC<FileCardProps> = ({
  fileId,
  file,
  fileEnv,
  onDelete,
  onView,
  badge,
  showCreatedAt,
  rightSlot,
  isUploading = false,
  disabled = false,
  dataLoc,
  floatingWindowPlacement,
  floatingWindowOffset,
}) => {
  if (isUploading && file) {
    return <FileCardUploading dataLoc={dataLoc} file={file} />;
  }

  if (fileId && fileEnv) {
    return (
      <RemoteFileCard
        badge={badge}
        dataLoc={dataLoc}
        disabled={disabled}
        floatingWindowOffset={floatingWindowOffset}
        floatingWindowPlacement={floatingWindowPlacement}
        rightSlot={rightSlot}
        fileEnv={fileEnv}
        fileId={fileId}
        localFile={file}
        showCreatedAt={showCreatedAt}
        onDelete={onDelete}
        onView={onView}
      />
    );
  }

  if (file) {
    return (
      <FileCardContent
        badge={badge}
        dataLoc={dataLoc}
        disabled={disabled}
        download={{ file }}
        floatingWindowOffset={floatingWindowOffset}
        floatingWindowPlacement={floatingWindowPlacement}
        rightSlot={resolveRightSlot(rightSlot, file.name)}
        metadata={<FileMetadata contentType={file.type} size={file.size} />}
        name={file.name}
        viewerFile={file}
        onDelete={onDelete}
        onView={onView}
      />
    );
  }

  return null;
};
